import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { pdfjs } from "react-pdf";
import { useSelector } from "react-redux";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import { checkPermission, hasAdminAccess, permissionType } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import "./CompanyOpsPage.scss";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TABS = [{ key: "public-holidays", label: "Public Holidays" }];
const MONTH_INDEX = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const dateToYMD = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDateDisplay = (value) => {
  const ymd = dateToYMD(value);
  if (!ymd) return "-";
  const [y, m, d] = ymd.split("-");
  return `${d}-${m}-${y}`;
};

const parseDateToken = (token, fallbackYear = new Date().getFullYear()) => {
  const cleaned = String(token || "").trim().replace(/\./g, "-").replace(/\//g, "-");
  if (!cleaned) return null;
  if (/^\d{1,2}-\d{1,2}-\d{2,4}$/.test(cleaned)) {
    const [dd, mm, yy] = cleaned.split("-");
    const year = yy.length === 2 ? `20${yy}` : yy;
    const iso = `${year}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    return dateToYMD(iso);
  }
  const parsed = new Date(cleaned);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  const textMatch = cleaned.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)(?:\s+(\d{4}))?$/i);
  if (textMatch) {
    const day = Number(textMatch[1]);
    const month = MONTH_INDEX[String(textMatch[2]).toLowerCase()];
    const year = Number(textMatch[3] || fallbackYear);
    if (day >= 1 && day <= 31 && month && year >= 1900) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return null;
};

const buildDateFromParts = (dayToken, monthToken, year) => {
  const day = Number(String(dayToken || "").replace(/[^\d]/g, ""));
  const month = MONTH_INDEX[String(monthToken || "").toLowerCase()];
  if (!day || !month || !year) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const extractHolidaysFromPdf = async (file, forceYear = null) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
  const rows = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();
    const groupedByY = new Map();
    textContent.items.forEach((item) => {
      const text = String(item.str || "").trim();
      if (!text) return;
      const x = Number(item.transform?.[4] || 0);
      const y = Number(item.transform?.[5] || 0);
      const yKey = Math.round(y / 3) * 3;
      const existing = groupedByY.get(yKey) || [];
      existing.push({ x, text });
      groupedByY.set(yKey, existing);
    });

    Array.from(groupedByY.entries())
      .sort((a, b) => b[0] - a[0])
      .forEach(([_, cells]) => {
        const sortedCells = cells.sort((a, b) => a.x - b.x).map((c) => c.text);
        if (sortedCells.length >= 4) {
          rows.push({
            sno: sortedCells[0],
            date: sortedCells[1],
            day: sortedCells[2],
            occasion: sortedCells.slice(3).join(" ").trim(),
          });
        }
      });
  }

  const seen = new Set();
  const parsedRows = [];
  const yearFromHeader =
    rows
      .map((r) => `${r.sno} ${r.date} ${r.day} ${r.occasion}`.match(/\b(20\d{2})\b/))
      .find(Boolean)?.[1] || String(new Date().getFullYear());
  const fallbackYear = Number(yearFromHeader);

  rows.forEach((row) => {
    const sno = String(row.sno || "").trim().toLowerCase();
    if (!/^\d+$/.test(sno)) return;
    if (!row.date || !row.occasion) return;

    const dateText = String(row.date).replace(/\s+/g, " ").trim();
    const dayText = String(row.day || "").replace(/\s+/g, " ").trim();
    const occasion = String(row.occasion || "").replace(/\s+/g, " ").trim();
    if (!occasion) return;

    if (dateText.includes("&")) {
      const complex = dateText.match(/^(\d{1,2})(?:st|nd|rd|th)?\s*&\s*(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)(?:\s+(\d{4}))?$/i);
      if (complex) {
        const [, d1, d2, monthWord, yearWord] = complex;
        const year = Number(yearWord || fallbackYear);
        const date1 = buildDateFromParts(d1, monthWord, year);
        const date2 = buildDateFromParts(d2, monthWord, year);
        const days = dayText.split("&").map((part) => part.trim()).filter(Boolean);
        [
          { holidayDate: date1, dayName: days[0] || null },
          { holidayDate: date2, dayName: days[1] || null },
        ].forEach((item) => {
          if (!item.holidayDate) return;
          const key = `${item.holidayDate}__${occasion.toLowerCase()}`;
          if (seen.has(key)) return;
          seen.add(key);
          parsedRows.push({
            holidayDate: item.holidayDate,
            holidayName: occasion,
            dayName: item.dayName,
            holidayType: "Public Holiday",
          });
        });
        return;
      }
    }

    const holidayDate = parseDateToken(dateText, fallbackYear);
    if (!holidayDate) return;
    const key = `${holidayDate}__${occasion.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    parsedRows.push({
      holidayDate,
      holidayName: occasion,
      dayName: dayText || null,
      holidayType: "Public Holiday",
    });
  });

  const normalizedRows = parsedRows.map((row) => {
    if (!forceYear) return row;
    const ymd = dateToYMD(row.holidayDate);
    if (!ymd) return row;
    const [, month, day] = ymd.split("-");
    return { ...row, holidayDate: `${forceYear}-${month}-${day}` };
  });
  return normalizedRows.sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
};

const initialForm = { holidayDate: "", holidayName: "", dayName: "", holidayType: "Public Holiday" };

const CompanyOpsPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const currentYear = new Date().getFullYear();
  const [activeTab, setActiveTab] = useState("public-holidays");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [yearFilter, setYearFilter] = useState(String(currentYear));
  const [importYear, setImportYear] = useState(String(currentYear));

  const canCreate = hasAdminAccess(user) || checkPermission(permissionType.COMPANY_OPS, "create");
  const canUpdate = hasAdminAccess(user) || checkPermission(permissionType.COMPANY_OPS, "update");
  const canDelete = hasAdminAccess(user) || checkPermission(permissionType.COMPANY_OPS, "delete");
  const showActionsColumn = canUpdate || canDelete;
  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear - 1; year <= currentYear + 5; year += 1) {
      years.push(String(year));
    }
    return years;
  }, [currentYear]);

  useEffect(() => {
    if (!canCreate) {
      const year = String(currentYear);
      setYearFilter(year);
      setImportYear(year);
    }
  }, [canCreate, currentYear]);

  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const response = await protectedApi({
        url: `${APIS.COMPANY_OPS_PUBLIC_HOLIDAYS}?year=${yearFilter}`,
        method: "GET",
      });
      setHolidays(response?.data?.data?.holidays || []);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to load public holidays.");
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const onDateChange = (value) => {
    const holidayDate = dateToYMD(value);
    const dayName = holidayDate
      ? new Date(holidayDate).toLocaleDateString("en-US", { weekday: "long" })
      : "";
    setForm((prev) => ({ ...prev, holidayDate, dayName }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.holidayDate || !form.holidayName.trim()) {
      showErrorToast("Holiday date and holiday name are required.");
      return;
    }
    try {
      const payload = {
        holidayDate: form.holidayDate,
        holidayName: form.holidayName.trim(),
        dayName: form.dayName || null,
        holidayType: form.holidayType || null,
      };
      if (editingId) {
        if (!canUpdate) return;
        await protectedApi({
          url: APIS.COMPANY_OPS_PUBLIC_HOLIDAYS.replace(/$/, `/${editingId}`),
          method: "PUT",
          data: payload,
        });
        showSuccessToast("Public holiday updated.");
      } else {
        if (!canCreate) return;
        await protectedApi({ url: APIS.COMPANY_OPS_PUBLIC_HOLIDAYS, method: "POST", data: payload });
        showSuccessToast("Public holiday created.");
      }
      setForm(initialForm);
      setEditingId(null);
      await loadHolidays();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to save public holiday.");
    }
  };

  const onEdit = (holiday) => {
    setEditingId(holiday.id);
    setForm({
      holidayDate: dateToYMD(holiday.holidayDate),
      holidayName: holiday.holidayName || "",
      dayName: holiday.dayName || "",
      holidayType: holiday.holidayType || "Public Holiday",
    });
  };

  const onDelete = async (id) => {
    if (!canDelete) return;
    try {
      await protectedApi({
        url: APIS.COMPANY_OPS_PUBLIC_HOLIDAYS.replace(/$/, `/${id}`),
        method: "DELETE",
      });
      showSuccessToast("Public holiday deleted.");
      await loadHolidays();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to delete public holiday.");
    }
  };

  const onImportPdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToast("Only PDF files are allowed.");
      return;
    }
    if (!canCreate) {
      showErrorToast("You do not have permission to import holidays.");
      return;
    }
    setImporting(true);
    try {
      const parsedRows = await extractHolidaysFromPdf(file, Number(importYear));
      if (!parsedRows.length) {
        showErrorToast("No holiday rows were detected in this PDF.");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("holidays", JSON.stringify(parsedRows));
      await protectedApi({
        url: APIS.COMPANY_OPS_PUBLIC_HOLIDAYS_IMPORT_PDF,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast(`Imported ${parsedRows.length} holiday row(s) from PDF.`);
      await loadHolidays();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to import holidays from PDF.");
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  const tableRows = useMemo(
    () =>
      holidays.map((holiday, index) => ({
        ...holiday,
        srNo: index + 1,
        holidayDate: dateToYMD(holiday.holidayDate),
      })),
    [holidays]
  );

  return (
    <div className="company-ops-page">
      <div className="mb-4">
        <h1 className="page-title">Public Holidays</h1>
        <p className="page-subtitle">Manage operational data like public holidays.</p>
      </div>

      <nav className="company-ops-tabs" aria-label="Company Ops sections">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`company-ops-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "public-holidays" ? (
        <section className="card-border card-body">
          <div className="company-ops-toolbar">
            <div className="company-ops-field">
              <label className="text-sm text-[#5f6d8a] block mb-1">Year</label>
              {canCreate ? (
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="search-input !text-black"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={yearFilter} readOnly className="search-input !text-black bg-[#f5f7fb]" />
              )}
            </div>
            {canCreate ? (
              <div className="company-ops-upload-group">
                <div className="company-ops-field">
                  <label className="text-sm text-[#5f6d8a] block mb-1">Upload year</label>
                  <select
                    value={importYear}
                    onChange={(e) => setImportYear(e.target.value)}
                    className="search-input !text-black company-ops-upload-year"
                    disabled={importing}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="company-ops-field company-ops-file-field">
                  <label className="text-sm text-[#5f6d8a] block mb-1">Import Holidays PDF</label>
                  <input type="file" accept="application/pdf" onChange={onImportPdf} disabled={importing} />
                </div>
                {importing ? (
                  <div className="company-ops-importing">
                    <Spin size="small" />
                    <span>Importing PDF and saving holidays...</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {(canCreate || canUpdate) && (
            <form className="company-ops-form" onSubmit={onSubmit}>
              <input
                type="date"
                value={form.holidayDate}
                onChange={(e) => onDateChange(e.target.value)}
                required
                className="search-input !text-black"
              />
              <input
                type="text"
                placeholder="Holiday name"
                value={form.holidayName}
                onChange={(e) => setForm((prev) => ({ ...prev, holidayName: e.target.value }))}
                required
                className="search-input !text-black"
              />
              <input
                type="text"
                placeholder="Day"
                value={form.dayName}
                onChange={(e) => setForm((prev) => ({ ...prev, dayName: e.target.value }))}
                className="search-input !text-black"
              />
              <input
                type="text"
                placeholder="Type"
                value={form.holidayType}
                onChange={(e) => setForm((prev) => ({ ...prev, holidayType: e.target.value }))}
                className="search-input !text-black"
              />
              <button type="submit" className="button bg-btn-primary text-white px-4 py-2 rounded-lg">
                {editingId ? "Update Holiday" : "Add Holiday"}
              </button>
              {editingId ? (
                <button
                  type="button"
                  className="button border px-4 py-2 rounded-lg"
                  onClick={() => {
                    setEditingId(null);
                    setForm(initialForm);
                  }}
                >
                  Cancel
                </button>
              ) : null}
            </form>
          )}

          <div className="company-ops-table-wrap">
            <table className="company-ops-table">
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Date</th>
                  <th>Holiday Name</th>
                  <th>Day</th>
                  <th>Type</th>
                  {showActionsColumn ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((holiday) => (
                  <tr key={holiday.id}>
                    <td>{holiday.srNo}</td>
                    <td>{formatDateDisplay(holiday.holidayDate)}</td>
                    <td>{holiday.holidayName || "-"}</td>
                    <td>{holiday.dayName || "-"}</td>
                    <td>{holiday.holidayType || "-"}</td>
                    {showActionsColumn ? (
                      <td>
                        <div className="company-ops-actions">
                          {canUpdate ? (
                            <button type="button" className="border rounded px-2 py-1" onClick={() => onEdit(holiday)}>
                              Edit
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              className="border rounded px-2 py-1 text-red-600 border-red-200"
                              onClick={() => onDelete(holiday.id)}
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
            {!tableRows.length ? (
              <p className="company-ops-empty">{loading ? "Loading public holidays..." : "No holidays found."}</p>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default CompanyOpsPage;
